/**
 * Malvay cart drawer (replaces Upcart).
 * Settings and text live in snippets/malvay-cart-drawer.liquid.
 *
 * Reads and changes the cart through Shopify's Ajax Cart API, and announces every
 * change with Shopify's standard cart events so the theme's cart bubble and
 * analytics stay in sync.
 */
const events = await import('@shopify/events').catch(() => null);
const moneyFormatting = await import('@theme/money-formatting').catch(() => null);

const dialog = document.getElementById('malvay-cart-drawer');
const settings = JSON.parse(dialog.querySelector('[data-ecd-settings]').textContent);
const shop = JSON.parse(dialog.querySelector('[data-ecd-shop]').textContent);
const text = settings.text;
const rootUrl = shop.routes.root.replace(/\/$/, '');
const ui = Object.fromEntries([...dialog.querySelectorAll('[data-ecd]')].map((el) => [el.dataset.ecd, el]));

const ICON_TRASH =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 5.5h13M8 5.5V3.5h4v2M5 5.5l.8 11h8.4l.8-11M8.3 8.5v5.5M11.7 8.5v5.5"/></svg>';

let cart = shop.cart;
const products = new Map(); // handle -> product JSON, once loaded
const productRequests = new Map(); // handle -> pending request
const selectedVariants = new Map(); // upsell handle -> variant id picked in the dropdown
const quantityTimers = new Map(); // line key -> debounce timer
const ownEvents = new WeakSet();
let queue = Promise.resolve();
let closeTimer;
let errorTimer;

/* ---------- helpers ---------- */

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
const fill = (template, values) => template.replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);
const onCartPage = () => location.pathname === shop.routes.cart;
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
// The currency converter app (Bucks) only converts prices on the page when asked to.
const convertPrices = () => window.bucksCC?.reConvert?.();

// Uses the shop's money format, which wraps amounts in <span class=money> so the
// currency converter app converts drawer prices like the rest of the store.
function price(cents) {
  const currency = cart.currency || shop.currency;
  if (moneyFormatting) return moneyFormatting.formatMoney(cents, shop.moneyFormat, currency);
  const formatted = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency });
  return escapeHtml(formatted.format(cents / 100));
}

function imageUrl(src, width) {
  if (!src) return '';
  const url = new URL(src, location.href);
  url.searchParams.set('width', width);
  return url.toString();
}

async function request(url, body) {
  const response = await fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.description || data.message || text.error);
  return data;
}

// Cart requests run one at a time so quick clicks can't overwrite each other.
function enqueue(task) {
  const run = queue.then(task);
  queue = run.catch(() => {});
  return run;
}

function showError(message) {
  ui.error.textContent = message;
  ui.error.hidden = false;
  clearTimeout(errorTimer);
  errorTimer = setTimeout(() => (ui.error.hidden = true), 6000);
}

/* ---------- data ---------- */

function loadProduct(handle) {
  if (!productRequests.has(handle)) {
    const pending = request(`${rootUrl}/products/${encodeURIComponent(handle)}.js`)
      .then((product) => products.set(handle, product).get(handle))
      .catch(() => null);
    productRequests.set(handle, pending);
  }
  return productRequests.get(handle);
}

function upsellHandles() {
  const inCart = new Set(cart.items.map((item) => item.handle));
  const handles = new Set();
  for (const rule of settings.upsells) {
    const triggered = rule.when === 'all' || rule.when.some((handle) => inCart.has(handle));
    if (!triggered) continue;
    for (const handle of rule.show) if (!inCart.has(handle)) handles.add(handle);
  }
  return [...handles];
}

// Product data supplies compare-at prices for cart lines and the upsell cards.
function loadProducts() {
  const handles = new Set(cart.items.map((item) => item.handle));
  if (cart.item_count) upsellHandles().forEach((handle) => handles.add(handle));
  return Promise.all([...handles].map(loadProduct));
}

async function refresh() {
  cart = await request(`${shop.routes.cart}.js`);
  await loadProducts();
  render();
}

const sync = () => enqueue(refresh).catch(() => showError(text.error));

// Tells the theme (cart bubble, analytics) about a change made in the drawer.
function announce(action, lines) {
  if (!events) return null;
  const deferred = events.CartLinesUpdateEvent.createPromise();
  const event = new events.CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise });
  ownEvents.add(event);
  dialog.dispatchEvent(event);
  return deferred;
}

function mutate(action, lines, task) {
  return enqueue(async () => {
    const deferred = announce(action, lines);
    ui.checkout.disabled = true;
    try {
      let updated;
      let didError = false;
      try {
        updated = await task();
      } catch (error) {
        didError = true;
        showError(error.message);
      }
      cart = updated ?? (await request(`${shop.routes.cart}.js`));
      deferred?.resolve({ cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError } });
      await loadProducts();
    } catch (error) {
      deferred?.reject(error);
      showError(text.error);
    } finally {
      ui.checkout.disabled = false;
      render();
    }
  });
}

function changeLine(key, quantity) {
  clearTimeout(quantityTimers.get(key));
  quantityTimers.delete(key);
  ui.items.querySelector(`[data-key="${CSS.escape(key)}"]`)?.classList.add('is-loading');
  return mutate(quantity > 0 ? 'update' : 'remove', [{ id: key, quantity }], () =>
    request(`${shop.routes.change}.js`, { id: key, quantity })
  );
}

function addVariant(button) {
  const id = Number(button.dataset.variantId);
  button.disabled = true;
  button.classList.add('is-loading');
  return mutate('add', [{ merchandiseId: id, quantity: 1 }], async () => {
    await request(`${shop.routes.add}.js`, { items: [{ id, quantity: 1 }] });
  });
}

/* ---------- rendering ---------- */

function render() {
  const focused = dialog.contains(document.activeElement) ? document.activeElement.dataset.focus : null;
  const isEmpty = cart.item_count === 0;

  ui.title.textContent = fill(text.title, { count: cart.item_count });
  ui.empty.hidden = !isEmpty;
  ui.footer.hidden = isEmpty;
  ui.items.innerHTML = cart.items.map(lineItemHtml).join('');
  renderRewards(isEmpty);
  renderUpsells(isEmpty);
  ui.discounts.hidden = !cart.total_discount;
  ui.discountAmount.innerHTML = `-${price(cart.total_discount)}`;
  ui.checkout.innerHTML = `<span>${fill(escapeHtml(text.checkout), { total: price(cart.total_price) })}</span>`;
  convertPrices();

  // Re-rendering replaces the buttons, so put keyboard focus back where it was.
  if (focused) (dialog.querySelector(`[data-focus="${CSS.escape(focused)}"]`) ?? dialog).focus();
}

function renderRewards(isEmpty) {
  const threshold = Math.round(settings.freeShippingThreshold * 100 * (Number(window.Shopify?.currency?.rate) || 1));
  ui.rewards.hidden = (isEmpty && !settings.freeShippingOnEmptyCart) || !threshold;
  if (ui.rewards.hidden) return;

  const remaining = threshold - cart.total_price;
  ui.rewardsText.innerHTML =
    remaining > 0
      ? fill(escapeHtml(text.freeShippingAway), { amount: `<strong>${price(remaining)}</strong>` })
      : escapeHtml(text.freeShippingUnlocked);
  ui.bar.style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
}

function lineItemHtml(item) {
  const key = escapeHtml(item.key);
  const title = item.product_title;
  const variant = products.get(item.handle)?.variants.find((v) => v.id === item.variant_id);
  const regularPrice = Math.max((variant?.compare_at_price || 0) * item.quantity, item.original_line_price);
  const savings = regularPrice - item.final_line_price;
  const details = [
    item.product_has_only_default_variant ? '' : item.variant_title,
    item.selling_plan_allocation?.selling_plan.name,
    ...Object.entries(item.properties ?? {})
      .filter(([name, value]) => value && !name.startsWith('_'))
      .map(([name, value]) => `${name}: ${value}`),
  ].filter(Boolean);

  return `
    <li class="ecd-item" data-key="${key}">
      <a class="ecd-item__media" href="${escapeHtml(item.url)}" tabindex="-1" aria-hidden="true">
        ${item.image ? `<img src="${escapeHtml(imageUrl(item.image, 200))}" alt="" width="88" height="88" loading="lazy">` : ''}
      </a>
      <div class="ecd-item__info">
        <div class="ecd-item__head">
          <a class="ecd-item__title" href="${escapeHtml(item.url)}">${escapeHtml(title)}</a>
          <button type="button" class="ecd-item__remove" data-ecd-remove data-focus="remove:${key}"
            aria-label="${escapeHtml(fill(text.remove, { title }))}">${ICON_TRASH}</button>
        </div>
        ${details.map((detail) => `<p class="ecd-item__detail">${escapeHtml(detail)}</p>`).join('')}
        <div class="ecd-item__foot">
          <div class="ecd-qty">
            <button type="button" class="ecd-qty__button" data-ecd-step="-1" data-focus="minus:${key}"
              aria-label="${escapeHtml(text.decrease)}">&minus;</button>
            <input class="ecd-qty__input" type="number" inputmode="numeric" min="0" value="${item.quantity}"
              data-focus="qty:${key}" aria-label="${escapeHtml(fill(text.quantity, { title }))}">
            <button type="button" class="ecd-qty__button" data-ecd-step="1" data-focus="plus:${key}"
              aria-label="${escapeHtml(text.increase)}">+</button>
          </div>
          <div class="ecd-item__prices">
            <p class="ecd-item__price">
              ${savings > 0 ? `<s class="ecd-compare">${price(regularPrice)}</s>` : ''}
              <span>${price(item.final_line_price)}</span>
            </p>
            ${savings > 0 ? `<p class="ecd-item__save">(${escapeHtml(text.save)} ${price(savings)})</p>` : ''}
          </div>
        </div>
      </div>
    </li>`;
}

function renderUpsells(isEmpty) {
  const list = isEmpty
    ? []
    : upsellHandles()
        .map((handle) => products.get(handle))
        .filter((product) => product?.available)
        .slice(0, settings.maxUpsells);
  ui.upsells.hidden = list.length === 0;
  ui.upsellList.innerHTML = list.map(upsellHtml).join('');
}

const priceHtml = (variant) =>
  `${variant.compare_at_price > variant.price ? `<s class="ecd-compare">${price(variant.compare_at_price)}</s>` : ''}
   <span>${price(variant.price)}</span>`;

function variantImage(product, variant) {
  return imageUrl(variant.featured_image?.src ?? product.featured_image, 200);
}

function upsellHtml(product) {
  const handle = escapeHtml(product.handle);
  const variants = product.variants.filter((v) => v.available);
  const variant = variants.find((v) => v.id === selectedVariants.get(product.handle)) ?? variants[0];
  const select =
    product.variants.length > 1
      ? `<select class="ecd-upsell__select" data-ecd-variant data-focus="variant:${handle}"
           aria-label="${escapeHtml(fill(text.chooseOption, { title: product.title }))}">
           ${variants
             .map((v) => `<option value="${v.id}"${v === variant ? ' selected' : ''}>${escapeHtml(v.title)}</option>`)
             .join('')}
         </select>`
      : '';

  return `
    <li class="ecd-upsell" data-handle="${handle}">
      <a class="ecd-upsell__media" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">
        <img src="${escapeHtml(variantImage(product, variant))}" alt="" width="90" height="90" loading="lazy">
      </a>
      <div class="ecd-upsell__info">
        <a class="ecd-upsell__title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
        <p class="ecd-upsell__price" data-ecd-price>${priceHtml(variant)}</p>
        <div class="ecd-upsell__actions">
          ${select}
          <button type="button" class="ecd__button ecd-upsell__add" data-ecd-add data-variant-id="${variant.id}"
            data-focus="add:${handle}">${escapeHtml(text.add)}</button>
        </div>
      </div>
    </li>`;
}

function selectVariant(select) {
  const card = select.closest('[data-handle]');
  const product = products.get(card.dataset.handle);
  const variant = product.variants.find((v) => v.id === Number(select.value));
  selectedVariants.set(product.handle, variant.id);
  card.querySelector('[data-ecd-add]').dataset.variantId = variant.id;
  card.querySelector('[data-ecd-price]').innerHTML = priceHtml(variant);
  card.querySelector('img').src = variantImage(product, variant);
  convertPrices();
}

/* ---------- open / close ---------- */

function open({ fetchCart = true } = {}) {
  clearTimeout(closeTimer);
  if (!dialog.open) {
    dialog.showModal();
    dialog.focus(); // focus the panel, not the close button
    dialog.getBoundingClientRect(); // commit the off-screen position so the slide-in animates
  }
  dialog.classList.add('is-open');
  render();
  if (fetchCart) sync();
}

function close() {
  dialog.classList.remove('is-open');
  clearTimeout(closeTimer);
  closeTimer = setTimeout(() => dialog.close(), reducedMotion() ? 0 : 300);
}

/* ---------- events ---------- */

dialog.querySelectorAll('[data-ecd-text]').forEach((el) => (el.textContent = text[el.dataset.ecdText]));
ui.close.setAttribute('aria-label', text.close);

dialog.addEventListener('click', (event) => {
  const target = event.target;
  // The drawer fills the whole <dialog>, so a click on the dialog itself is a click on the backdrop.
  if (target === dialog || target.closest('[data-ecd-close]')) return close();

  const line = target.closest('[data-key]');
  if (target.closest('[data-ecd-remove]')) return changeLine(line.dataset.key, 0);

  const step = target.closest('[data-ecd-step]');
  if (step) {
    const input = line.querySelector('.ecd-qty__input');
    const quantity = Math.max(0, Number(input.value) + Number(step.dataset.ecdStep));
    input.value = quantity;
    clearTimeout(quantityTimers.get(line.dataset.key));
    quantityTimers.set(
      line.dataset.key,
      setTimeout(() => changeLine(line.dataset.key, quantity), 350)
    );
    return;
  }

  const add = target.closest('[data-ecd-add]');
  if (add) addVariant(add);
});

dialog.addEventListener('change', (event) => {
  const target = event.target;
  if (target.matches('[data-ecd-variant]')) selectVariant(target);
  if (target.matches('.ecd-qty__input')) {
    changeLine(target.closest('[data-key]').dataset.key, Math.max(0, parseInt(target.value, 10) || 0));
  }
});

dialog.addEventListener('cancel', (event) => {
  event.preventDefault();
  close();
});

dialog.addEventListener('close', () => dialog.classList.remove('is-open'));

// Open the drawer instead of going to /cart. The cart page itself still works.
document.addEventListener('click', (event) => {
  const link = event.target.closest?.('a[href]');
  if (!link || event.defaultPrevented || event.button !== 0 || onCartPage()) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const url = new URL(link.href, location.href);
  if (url.origin !== location.origin || url.pathname !== shop.routes.cart || url.search) return;
  event.preventDefault();
  open();
});

// Open the drawer after the theme's own Add to cart buttons finish.
if (events) {
  document.addEventListener(events.StandardEvents.cartLinesUpdate, (event) => {
    if (ownEvents.has(event) || event.action !== 'add' || !settings.openOnAddToCart || onCartPage()) return;
    event.promise
      ?.then((result) => {
        if (!result?.detail?.didError) return enqueue(refresh).then(() => open({ fetchCart: false }));
      })
      .catch(() => {});
  });
}

// Warm up product data so the first open already shows compare-at prices and upsells.
const warmUp = () => cart.item_count && loadProducts();
if ('requestIdleCallback' in window) requestIdleCallback(warmUp);
else setTimeout(warmUp, 1500);
